/* eslint-disable @typescript-eslint/no-explicit-any */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

/**
 * FleetONE — Vehicles PDF PRO (A4 nativo, corporativo)
 * - NÃO imprime HTML.
 * - Desenha layout profissional usando jsPDF + autoTable.
 * - INCLUI detalhamento completo por veículo (manutenções, OS, abastecimentos, etc)
 */

/* --------------------------
 * Types - IMPORTADOS DO CONSOLIDATED
 * -------------------------- */

export type PdfMode = "executive" | "top-n" | "complete";

export type FleetVehicleCostCategory =
  | "fuel"
  | "maintenance"
  | "tires"
  | "expenses"
  | "documents"
  | "trips";

export type VehicleCostBreakdown = Record<FleetVehicleCostCategory, number>;

export type FuelItem = {
  occurredAt: string;
  station?: string | null;
  liters?: number | null;
  priceUnit?: number | null;
  totalValue?: number | null;
  odometerKm?: number | null;
  notes?: string | null;
};

export type MaintenanceItem = {
  orderNumber: string;
  planName?: string | null;
  executedAt: string;
  supplier?: string | null;
  type?: string | null;
  totalCost: number;
  items?: Array<{ name: string; qty?: number; unitValue?: number; totalValue?: number }>;
};

export type ExpenseItem = {
  occurredAt: string;
  category?: string | null;
  supplier?: string | null;
  totalCost: number;
  notes?: string | null;
  description?: string | null;
};

export type TireItem = {
  occurredAt: string;
  supplier?: string | null;
  tireModel?: string | null;
  qty?: number | null;
  totalCost: number;
  notes?: string | null;
};

export type DocumentItem = {
  name: string;
  type?: string | null;
  expiryDate?: string | null;
  paidAt?: string | null;
  totalCost?: number | null;
  status?: string | null;
};

export type TripItem = {
  startAt: string;
  endAt?: string | null;
  distanceKm: number;
  driverName?: string | null;
  origin?: string | null;
  destination?: string | null;
};

export type VehicleDetailData = {
  vehicle_id: string;
  vehicle_label: string;
  periodLabel?: string;

  summary: {
    totalCost: number;
    costPerKm: number;
    km: number;
    breakdown: VehicleCostBreakdown;
  };

  fuel: FuelItem[];
  maintenance: MaintenanceItem[];
  tires: TireItem[];
  expenses: ExpenseItem[];
  documents: DocumentItem[];
  trips: TripItem[];
};

export type PdfVehicleSummary = {
  vehicle_id: string;
  vehicle_label: string;
  model?: string;
  km: number;
  cost: number;
  costPerKm: number;
  efficiency?: number;
  fuelCost?: number;
  maintenanceCost?: number;
  trips?: number;
  breakdown?: Partial<VehicleCostBreakdown>;
};

export type VehicleRankingItem = {
  vehicle_id: string;
  vehicle_label: string;
  model: string;
  trips: number;
  km: number;
  fuelCost: number;
  maintenanceCost: number;
  totalCost: number;
  costPerKm: number;
  efficiency: number;
};

export type VehiclePdfKpis = {
  totalVehicles: number;
  activeVehicles: number;
  totalDistance: number;
  totalCost: number;
  fuelCost: number;
  maintenanceCost: number;
  avgCostPerKm: number;
  avgEfficiency: number;
  avgTripsPerVehicle: number;
};

export type VehiclesPdfInput = {
  companyName: string;
  periodLabel: string;
  generatedAtLabel: string;
  
  kpis: VehiclePdfKpis;
  vehicleRanking: VehicleRankingItem[];
  
  insights: string[];
  conclusionText: string;

  // NOVOS: Para detalhamento completo por veículo
  vehiclesSummary: PdfVehicleSummary[];
  fetchVehicleDetail: (vehicleId: string) => Promise<VehicleDetailData>;
  options: { mode: PdfMode; topN?: number; maxVehicles?: number };
};

/* --------------------------
 * Theme / Helpers
 * -------------------------- */

const A4 = { w: 210, h: 297 };
const M = { l: 14, r: 14, t: 14, b: 14 };
const COLORS = {
  ink: [15, 23, 42] as [number, number, number],
  muted: [100, 116, 139] as [number, number, number],
  border: [226, 232, 240] as [number, number, number],
  panel: [248, 250, 252] as [number, number, number],
  header: [2, 6, 23] as [number, number, number],
  headRow: [241, 245, 249] as [number, number, number],
  zebra: [250, 250, 251] as [number, number, number],
  ok: [16, 185, 129] as [number, number, number],
  warn: [245, 158, 11] as [number, number, number],
  crit: [239, 68, 68] as [number, number, number],
};

function moneyBRL(n: number) {
  return (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function numberBR(n: number) {
  return (Number(n) || 0).toLocaleString("pt-BR");
}

function setFont(doc: jsPDF, weight: "normal" | "bold" = "normal") {
  doc.setFont("helvetica", weight);
}

function drawHeader(doc: jsPDF, title: string, subtitle: string) {
  doc.setFillColor(...COLORS.header);
  doc.roundedRect(M.l, M.t, A4.w - M.l - M.r, 22, 4, 4, "F");

  doc.setTextColor(255, 255, 255);
  setFont(doc, "bold");
  doc.setFontSize(12);
  doc.text("FleetONE", M.l + 6, M.t + 8);

  doc.setFontSize(14);
  doc.text(title, M.l + 6, M.t + 16);

  setFont(doc, "normal");
  doc.setFontSize(9);
  doc.setTextColor(226, 232, 240);
  doc.text(subtitle, M.l + 6, M.t + 20);

  doc.setTextColor(...COLORS.ink);
}

function drawFooter(doc: jsPDF, meta: { generatedAtLabel: string }, pageNum: number, totalPages: number) {
  const y = A4.h - 10;

  doc.setDrawColor(...COLORS.border);
  doc.line(M.l, y - 4, A4.w - M.r, y - 4);

  doc.setTextColor(...COLORS.muted);
  doc.setFontSize(8);
  setFont(doc, "normal");
  doc.text(`Gerado em ${meta.generatedAtLabel}`, M.l, y);
  doc.text(`Página ${pageNum} de ${totalPages}`, A4.w - M.r, y, { align: "right" });

  doc.setTextColor(...COLORS.ink);
}

function panel(doc: jsPDF, x: number, y: number, w: number, h: number) {
  doc.setDrawColor(...COLORS.border);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, w, h, 4, 4, "FD");
}

function kpiCard(doc: jsPDF, x: number, y: number, w: number, h: number, opts: {
  label: string;
  value: string;
  sub?: string;
  badge?: { text: string; tone: "ok" | "warn" | "crit" };
}) {
  panel(doc, x, y, w, h);

  doc.setTextColor(...COLORS.muted);
  doc.setFontSize(8);
  setFont(doc, "normal");
  doc.text(opts.label, x + 4, y + 6);

  doc.setTextColor(...COLORS.ink);
  doc.setFontSize(12);
  setFont(doc, "bold");
  doc.text(opts.value, x + 4, y + 14);

  if (opts.sub) {
    doc.setTextColor(...COLORS.muted);
    doc.setFontSize(8);
    setFont(doc, "normal");
    doc.text(opts.sub, x + 4, y + 19);
  }

  if (opts.badge) {
    const col =
      opts.badge.tone === "crit" ? COLORS.crit : opts.badge.tone === "warn" ? COLORS.warn : COLORS.ok;

    doc.setFillColor(...col);
    doc.roundedRect(x + w - 24, y + 4, 20, 6.5, 3, 3, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    setFont(doc, "bold");
    doc.text(opts.badge.text, x + w - 14, y + 8.5, { align: "center" });

    doc.setTextColor(...COLORS.ink);
  }
}

function sectionTitle(doc: jsPDF, x: number, y: number, title: string, subtitle?: string) {
  setFont(doc, "bold");
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.ink);
  doc.text(title, x, y);

  if (subtitle) {
    setFont(doc, "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.muted);
    doc.text(subtitle, x, y + 4);
  }
  doc.setTextColor(...COLORS.ink);
}

function tablePro(doc: jsPDF, opts: {
  startY: number;
  head: string[];
  body: any[][];
  marginLeft?: number;
  marginRight?: number;
  marginTopOnNewPage?: number;
  onEveryPageHeader?: () => void;
}) {
  const topMarginForNewPages = opts.marginTopOnNewPage ?? M.t + 44;
  
  autoTable(doc, {
    startY: opts.startY,
    head: [opts.head],
    body: opts.body,
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: 3,
      textColor: COLORS.ink,
      lineColor: COLORS.border,
    },
    headStyles: {
      fillColor: COLORS.headRow,
      textColor: COLORS.muted,
      fontStyle: "bold",
    },
    alternateRowStyles: { fillColor: COLORS.zebra },
    margin: {
      left: opts.marginLeft ?? M.l,
      right: opts.marginRight ?? M.r,
      top: topMarginForNewPages,
    },
    didDrawPage: () => {
      opts.onEveryPageHeader?.();
    },
  });
  return (doc as any).lastAutoTable?.finalY ?? opts.startY;
}

function emptyBlockExec(doc: jsPDF, y: number, msg: string): number {
  doc.setFillColor(...COLORS.panel);
  doc.roundedRect(M.l, y, A4.w - M.l - M.r, 12, 2, 2, "F");
  doc.setTextColor(...COLORS.muted);
  doc.setFontSize(9);
  setFont(doc, "normal");
  doc.text(msg, M.l + 4, y + 8);
  doc.setTextColor(...COLORS.ink);
  return y + 16;
}

function severityBadge(costPerKm: number) {
  if (costPerKm >= 2.5) return { text: "Critico", tone: "crit" as const };
  if (costPerKm >= 1.5) return { text: "Atencao", tone: "warn" as const };
  return { text: "OK", tone: "ok" as const };
}

/* --------------------------
 * Vehicle Detail Page Helpers
 * -------------------------- */

function ensureSpace(
  doc: jsPDF,
  currentY: number,
  neededHeight: number,
  onNewPage: () => void
): number {
  const bottomLimit = A4.h - M.b - 12;
  if (currentY + neededHeight > bottomLimit) {
    doc.addPage();
    onNewPage();
    return M.t + 50;
  }
  return currentY;
}

function emptyBlock(doc: jsPDF, y: number, msg: string): number {
  doc.setFillColor(...COLORS.panel);
  doc.roundedRect(M.l, y, A4.w - M.l - M.r, 12, 2, 2, "F");
  doc.setTextColor(...COLORS.muted);
  doc.setFontSize(9);
  setFont(doc, "normal");
  doc.text(msg, M.l + 4, y + 8);
  doc.setTextColor(...COLORS.ink);
  return y + 16;
}

function drawCategory(
  doc: jsPDF,
  opts: {
    title: string;
    columns: string[];
    rows: any[][];
    emptyMsg: string;
  },
  y: number,
  onNewPage: () => void
): number {
  y = ensureSpace(doc, y, 18, onNewPage);
  sectionTitle(doc, M.l, y, opts.title);
  y += 6;

  if (!opts.rows || opts.rows.length === 0) {
    return emptyBlock(doc, y, opts.emptyMsg);
  }

  y = ensureSpace(doc, y, 24, onNewPage);
  const finalY = tablePro(doc, {
    startY: y,
    head: opts.columns,
    body: opts.rows,
    marginTopOnNewPage: M.t + 50,
    onEveryPageHeader: onNewPage,
  });

  return finalY + 6;
}

function drawVehicleHeaderChip(doc: jsPDF, vehicleLabel: string) {
  const y0 = M.t + 26;
  panel(doc, M.l, y0, A4.w - M.l - M.r, 14);
  setFont(doc, "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.muted);
  doc.text("Veículo:", M.l + 4, y0 + 9);
  setFont(doc, "bold");
  doc.setTextColor(...COLORS.ink);
  doc.text(vehicleLabel, M.l + 22, y0 + 9);
}

function drawStackedVehicleKpis(
  doc: jsPDF,
  sum: { totalCost: number; costPerKm: number; km: number },
  y: number
): number {
  const gap = 4;
  const w = (A4.w - M.l - M.r - gap * 2) / 3;
  const h = 20;

  kpiCard(doc, M.l, y, w, h, {
    label: "Custo total do veículo",
    value: moneyBRL(sum.totalCost),
  });
  kpiCard(doc, M.l + (w + gap), y, w, h, {
    label: "Custo por km",
    value: moneyBRL(sum.costPerKm),
    badge: severityBadge(sum.costPerKm),
  });
  kpiCard(doc, M.l + 2 * (w + gap), y, w, h, {
    label: "Km no período",
    value: numberBR(sum.km),
  });

  return y + h + 6;
}

function drawStackedCostComposition(
  doc: jsPDF,
  breakdown: Partial<VehicleCostBreakdown> | undefined,
  totalCost: number,
  y: number
): number {
  sectionTitle(doc, M.l, y, "Composição do custo");
  y += 8;

  const b = breakdown ?? { fuel: 0, maintenance: 0, tires: 0, expenses: 0, documents: 0, trips: 0 };
  const total = totalCost || 1;

  const items: Array<{ k: FleetVehicleCostCategory; label: string }> = [
    { k: "fuel", label: "Combustível" },
    { k: "maintenance", label: "Manutenção" },
    { k: "tires", label: "Pneus" },
    { k: "expenses", label: "Despesas" },
    { k: "documents", label: "Documentos" },
    { k: "trips", label: "Viagens" },
  ];

  const barX = M.l + 70;
  const barW = A4.w - M.l - M.r - 72;
  const rowH = 7;

  items.forEach((it) => {
    const val = b[it.k] ?? 0;
    const pct = Math.round((val / total) * 100);

    doc.setFontSize(8.5);
    setFont(doc, "normal");
    doc.setTextColor(...COLORS.ink);
    doc.text(it.label, M.l, y);

    doc.setTextColor(...COLORS.muted);
    doc.text(`${moneyBRL(val)}  ${pct}%`, M.l + 28, y);

    doc.setFillColor(...COLORS.panel);
    doc.roundedRect(barX, y - 3.5, barW, 4, 2, 2, "F");

    doc.setFillColor(...COLORS.warn);
    const pw = Math.max(1, (barW * pct) / 100);
    doc.roundedRect(barX, y - 3.5, pw, 4, 2, 2, "F");

    y += rowH;
  });

  doc.setTextColor(...COLORS.ink);
  return y + 4;
}

/* --------------------------
 * Executive Page Functions
 * -------------------------- */

function drawRankingTable(doc: jsPDF, ranking: VehicleRankingItem[], startY: number): number {
  sectionTitle(doc, M.l, startY, "Ranking de Veículos por Custo Total", "Top 10 — Combustível + Manutenção");
  const y = startY + 8;

  const rows = (ranking ?? []).slice(0, 10).map((r) => ([
    r.vehicle_label,
    r.model,
    String(r.trips),
    numberBR(r.km),
    moneyBRL(r.fuelCost),
    moneyBRL(r.maintenanceCost),
    moneyBRL(r.totalCost),
    (Number(r.costPerKm) || 0).toFixed(2),
  ]));

  if (rows.length === 0) {
    return emptyBlockExec(doc, y, "Sem dados de veículos no período.");
  }

  const finalY = tablePro(doc, {
    startY: y,
    head: ["Veículo", "Modelo", "Viagens", "Km", "Combustível", "Manutenção", "Total", "R$/Km"],
    body: rows,
  });

  return finalY + 8;
}

function drawEfficiencyTable(doc: jsPDF, ranking: VehicleRankingItem[], startY: number): number {
  sectionTitle(doc, M.l, startY, "Análise de Eficiência por Veículo", "Km/L e custo operacional");
  const y = startY + 8;

  const rows = (ranking ?? [])
    .filter(r => r.efficiency > 0)
    .sort((a, b) => b.efficiency - a.efficiency)
    .slice(0, 10)
    .map((r) => ([
      r.vehicle_label,
      r.model,
      numberBR(r.km),
      `${r.efficiency.toFixed(1)} km/L`,
      moneyBRL(r.fuelCost),
      (Number(r.costPerKm) || 0).toFixed(2),
    ]));

  if (rows.length === 0) {
    return emptyBlockExec(doc, y, "Sem dados de eficiência no período.");
  }

  const finalY = tablePro(doc, {
    startY: y,
    head: ["Veículo", "Modelo", "Km", "Eficiência", "Combustível", "R$/Km"],
    body: rows,
  });

  return finalY + 8;
}

function drawInsightsBlock(doc: jsPDF, insights: string[], startY: number): number {
  const list = (insights ?? []).slice(0, 6);
  const insightsH = 10 + Math.max(list.length, 3) * 5.5;
  
  panel(doc, M.l, startY, A4.w - M.l - M.r, insightsH);
  sectionTitle(doc, M.l + 4, startY + 8, "Principais Insights do Período");

  setFont(doc, "normal");
  doc.setFontSize(9);

  let ly = startY + 16;
  list.forEach((t) => {
    doc.setTextColor(...COLORS.muted);
    doc.text("•", M.l + 6, ly);
    doc.setTextColor(...COLORS.ink);
    const wrapped = doc.splitTextToSize(String(t), A4.w - M.l - M.r - 16);
    doc.text(wrapped, M.l + 10, ly);
    ly += wrapped.length * 4.5;
  });

  return startY + insightsH + 6;
}

function drawConclusionBlock(doc: jsPDF, data: VehiclesPdfInput, startY: number): number {
  const summaryText = `A frota analisada possui ${data.kpis.totalVehicles} veículos, com custo total de ${moneyBRL(data.kpis.totalCost)} e quilometragem de ${numberBR(data.kpis.totalDistance)} km no período. O custo médio por km é de ${moneyBRL(data.kpis.avgCostPerKm)}. A eficiência média da frota é de ${data.kpis.avgEfficiency.toFixed(1)} km/L.`;

  const wrappedSummary = doc.splitTextToSize(summaryText, A4.w - M.l - M.r - 12);
  const conclusionH = 24 + wrappedSummary.length * 4;

  panel(doc, M.l, startY, A4.w - M.l - M.r, conclusionH);
  sectionTitle(doc, M.l + 4, startY + 8, "Conclusão Executiva");

  doc.setFontSize(9);
  setFont(doc, "normal");
  doc.setTextColor(...COLORS.ink);
  doc.text(wrappedSummary, M.l + 4, startY + 16);

  return startY + conclusionH + 6;
}

function addExecutivePage(doc: jsPDF, data: VehiclesPdfInput) {
  const title = "Relatório de Veículos — Visão Executiva";
  const subtitle = `${data.periodLabel} • ${data.companyName}`;

  drawHeader(doc, title, subtitle);

  // KPIs (2 linhas x 3 col)
  const topY = 40;
  const gap = 4;
  const w = (A4.w - M.l - M.r - gap * 2) / 3;
  const h = 22;

  const sev = severityBadge(data.kpis.avgCostPerKm);

  kpiCard(doc, M.l, topY, w, h, {
    label: "Total de Veículos",
    value: String(data.kpis.totalVehicles),
    sub: `${data.kpis.activeVehicles} ativos`,
  });

  kpiCard(doc, M.l + w + gap, topY, w, h, {
    label: "Distância Total",
    value: `${numberBR(data.kpis.totalDistance)} km`,
  });

  kpiCard(doc, M.l + (w + gap) * 2, topY, w, h, {
    label: "Custo Total",
    value: moneyBRL(data.kpis.totalCost),
    badge: sev,
  });

  // Segunda linha
  const row2Y = topY + h + gap;

  kpiCard(doc, M.l, row2Y, w, h, {
    label: "Combustível",
    value: moneyBRL(data.kpis.fuelCost),
    sub: `${((data.kpis.fuelCost / (data.kpis.totalCost || 1)) * 100).toFixed(0)}% do total`,
  });

  kpiCard(doc, M.l + w + gap, row2Y, w, h, {
    label: "Manutenção",
    value: moneyBRL(data.kpis.maintenanceCost),
    sub: `${((data.kpis.maintenanceCost / (data.kpis.totalCost || 1)) * 100).toFixed(0)}% do total`,
  });

  kpiCard(doc, M.l + (w + gap) * 2, row2Y, w, h, {
    label: "Custo Médio por Km",
    value: `R$ ${data.kpis.avgCostPerKm.toFixed(2)}`,
    sub: `Efic. ${data.kpis.avgEfficiency.toFixed(1)} km/L`,
  });

  let yPos = row2Y + h + 12;

  // Ranking
  yPos = drawRankingTable(doc, data.vehicleRanking, yPos);

  // Eficiência
  if (yPos + 60 > A4.h - 30) {
    doc.addPage();
    drawHeader(doc, "Relatório de Veículos — Eficiência", subtitle);
    yPos = 44;
  }
  yPos = drawEfficiencyTable(doc, data.vehicleRanking, yPos);

  // Insights
  if (data.insights.length > 0) {
    if (yPos + 50 > A4.h - 30) {
      doc.addPage();
      drawHeader(doc, "Relatório de Veículos — Insights", subtitle);
      yPos = 44;
    }
    yPos = drawInsightsBlock(doc, data.insights, yPos);
  }

  // Conclusão
  if (yPos + 40 > A4.h - 30) {
    doc.addPage();
    drawHeader(doc, "Relatório de Veículos — Conclusão", subtitle);
    yPos = 44;
  }
  drawConclusionBlock(doc, data, yPos);
}

/* --------------------------
 * Vehicle Detail Page
 * -------------------------- */

function addVehiclePage(doc: jsPDF, meta: VehiclesPdfInput, vehicle: PdfVehicleSummary, detail: VehicleDetailData) {
  doc.addPage();

  const title = "Detalhamento por Veículo — Custos e Itens";
  const subtitle = `${meta.periodLabel} • ${meta.companyName}`;

  const onNewPage = () => {
    drawHeader(doc, title, subtitle);
    drawVehicleHeaderChip(doc, detail.vehicle_label || vehicle.vehicle_label);
  };

  onNewPage();

  let y = M.t + 44;

  // 1) Summary com fallback
  const sum = detail.summary ?? {
    totalCost: vehicle.cost ?? 0,
    costPerKm: vehicle.costPerKm ?? 0,
    km: vehicle.km ?? 0,
    breakdown: (vehicle.breakdown as VehicleCostBreakdown) ?? { fuel: 0, maintenance: 0, tires: 0, expenses: 0, documents: 0, trips: 0 },
  };

  // 2) KPIs (3 cards)
  y = ensureSpace(doc, y, 28, onNewPage);
  y = drawStackedVehicleKpis(doc, sum, y);

  // 3) Composição de custo
  y = ensureSpace(doc, y, 54, onNewPage);
  y = drawStackedCostComposition(doc, sum.breakdown, sum.totalCost, y);

  // 4) TABELAS EMPILHADAS

  // Manutenção
  y = drawCategory(doc, {
    title: "Manutenção / OS",
    columns: ["OS", "Plano", "Data", "Fornecedor", "Valor"],
    rows: (detail.maintenance ?? []).slice(0, 20).map((m) => [
      m.orderNumber || "-",
      m.planName || "-",
      m.executedAt || "-",
      m.supplier || "-",
      moneyBRL(m.totalCost || 0),
    ]),
    emptyMsg: "Sem ordens de serviço no período.",
  }, y, onNewPage);

  // Combustível
  y = drawCategory(doc, {
    title: "Combustível",
    columns: ["Data", "Posto", "Litros", "R$/L", "Valor"],
    rows: (detail.fuel ?? []).slice(0, 25).map((f) => {
      const v = (f.totalValue ?? ((f.liters || 0) * (f.priceUnit || 0))) || 0;
      return [
        f.occurredAt || "-",
        f.station || "-",
        f.liters != null ? String(Number(f.liters).toFixed(1)) : "-",
        f.priceUnit != null ? String(Number(f.priceUnit).toFixed(2)) : "-",
        moneyBRL(v),
      ];
    }),
    emptyMsg: "Sem abastecimentos no período.",
  }, y, onNewPage);

  // Pneus
  y = drawCategory(doc, {
    title: "Pneus",
    columns: ["Data", "Descrição", "Valor"],
    rows: (detail.tires ?? []).slice(0, 15).map((t) => [
      t.occurredAt || "-",
      t.tireModel || t.notes || "-",
      moneyBRL(t.totalCost || 0),
    ]),
    emptyMsg: "Sem lançamentos de pneus no período.",
  }, y, onNewPage);

  // Despesas
  y = drawCategory(doc, {
    title: "Despesas",
    columns: ["Data", "Categoria", "Valor"],
    rows: (detail.expenses ?? []).slice(0, 15).map((e) => [
      e.occurredAt || "-",
      e.category || e.description || "-",
      moneyBRL(e.totalCost || 0),
    ]),
    emptyMsg: "Sem despesas no período.",
  }, y, onNewPage);

  // Documentos
  y = drawCategory(doc, {
    title: "Documentos",
    columns: ["Documento", "Tipo", "Venc.", "Status"],
    rows: (detail.documents ?? []).slice(0, 15).map((d) => [
      d.name || "-",
      d.type || "-",
      d.expiryDate || "-",
      d.status || "-",
    ]),
    emptyMsg: "Sem documentos no período.",
  }, y, onNewPage);

  // Viagens
  y = drawCategory(doc, {
    title: "Viagens",
    columns: ["Início", "Fim", "Km", "Motorista", "Rota"],
    rows: (detail.trips ?? []).slice(0, 15).map((t) => [
      t.startAt || "-",
      t.endAt || "-",
      numberBR(t.distanceKm || 0),
      t.driverName || "-",
      [t.origin, t.destination].filter(Boolean).join(" → ") || "-",
    ]),
    emptyMsg: "Sem viagens no período.",
  }, y, onNewPage);
}

/* --------------------------
 * Exports
 * -------------------------- */

export async function generateVehiclesPDF_Pro(input: VehiclesPdfInput): Promise<Blob> {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  
  addExecutivePage(doc, input);

  // Se modo "executive", não adiciona detalhamento
  const mode = input.options?.mode ?? "top-n";
  if (mode !== "executive") {
    const topN = input.options?.topN ?? 10;
    const maxVehicles = input.options?.maxVehicles ?? 80;

    let vehiclesToDetail: PdfVehicleSummary[] =
      mode === "top-n"
        ? (input.vehiclesSummary ?? []).slice(0, topN)
        : (input.vehiclesSummary ?? []);

    const truncated = vehiclesToDetail.length > maxVehicles;
    if (truncated) vehiclesToDetail = vehiclesToDetail.slice(0, maxVehicles);

    // Render por veículo
    for (const v of vehiclesToDetail) {
      const detail = await input.fetchVehicleDetail(v.vehicle_id);
      addVehiclePage(doc, input, v, detail);
    }

    // Aviso de truncamento (opcional)
    if (truncated) {
      doc.addPage();
      drawHeader(
        doc,
        "Aviso de truncamento",
        `${input.periodLabel} • ${input.companyName}`
      );
      panel(doc, M.l, 45, A4.w - M.l - M.r, 40);
      sectionTitle(doc, M.l + 4, 55, "Relatório completo limitado", "Proteção contra PDFs gigantes");
      setFont(doc, "normal");
      doc.setFontSize(10);
      doc.setTextColor(...COLORS.ink);
      doc.text(
        doc.splitTextToSize(
          `Este PDF foi limitado a ${maxVehicles} veículos detalhados para garantir performance e legibilidade. ` +
            `Use o modo Top N para focar nos veículos de maior custo ou aumente o limite de veículos no filtro.`,
          A4.w - M.l - M.r - 8
        ),
        M.l + 4,
        65
      );
    }
  }

  // Footer em todas as páginas
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc, { generatedAtLabel: input.generatedAtLabel }, i, totalPages);
  }

  return doc.output("blob");
}

export function buildVehiclesPdfInput(params: {
  vehicleData: any[];
  periodLabel: string;
  companyName: string;
  // NOVOS parâmetros para detalhamento
  vehiclesSummary?: PdfVehicleSummary[];
  allVehiclesDetails?: Map<string, VehicleDetailData>;
  fetchVehicleDetail?: (vehicleId: string) => Promise<VehicleDetailData>;
  options?: { mode: PdfMode; topN?: number; maxVehicles?: number };
}): VehiclesPdfInput {
  const { vehicleData, periodLabel, companyName } = params;
  
  const generatedAtLabel = format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  
  // Calcular KPIs
  const totalVehicles = vehicleData.length;
  const activeVehicles = totalVehicles;
  const totalDistance = vehicleData.reduce((sum, v) => sum + (v.total_distance || 0), 0);
  const fuelCost = vehicleData.reduce((sum, v) => sum + (v.total_fuel_cost || 0), 0);
  const maintenanceCost = vehicleData.reduce((sum, v) => sum + (v.total_maintenance_cost || 0), 0);
  const totalCost = fuelCost + maintenanceCost;
  const avgCostPerKm = totalDistance > 0 ? totalCost / totalDistance : 0;
  const avgEfficiency = totalVehicles > 0 
    ? vehicleData.reduce((sum, v) => sum + (v.fuel_efficiency || 0), 0) / totalVehicles 
    : 0;
  const avgTripsPerVehicle = totalVehicles > 0
    ? vehicleData.reduce((sum, v) => sum + (v.total_trips || 0), 0) / totalVehicles
    : 0;

  // Ranking
  const vehicleRanking: VehicleRankingItem[] = vehicleData
    .map(v => ({
      vehicle_id: v.vehicle_id,
      vehicle_label: v.vehicle_plate || "-",
      model: v.vehicle_model || "-",
      trips: v.total_trips || 0,
      km: v.total_distance || 0,
      fuelCost: v.total_fuel_cost || 0,
      maintenanceCost: v.total_maintenance_cost || 0,
      totalCost: (v.total_fuel_cost || 0) + (v.total_maintenance_cost || 0),
      costPerKm: v.total_distance > 0 
        ? ((v.total_fuel_cost || 0) + (v.total_maintenance_cost || 0)) / v.total_distance 
        : 0,
      efficiency: v.fuel_efficiency || 0,
    }))
    .sort((a, b) => b.totalCost - a.totalCost);

  // VehiclesSummary para PDF (reutiliza dados ou constrói a partir de vehicleData)
  const vehiclesSummary: PdfVehicleSummary[] = params.vehiclesSummary ?? vehicleRanking.map(v => ({
    vehicle_id: v.vehicle_id,
    vehicle_label: v.vehicle_label,
    model: v.model,
    km: v.km,
    cost: v.totalCost,
    costPerKm: v.costPerKm,
    efficiency: v.efficiency,
    fuelCost: v.fuelCost,
    maintenanceCost: v.maintenanceCost,
    trips: v.trips,
    breakdown: {
      fuel: v.fuelCost,
      maintenance: v.maintenanceCost,
      tires: 0,
      expenses: 0,
      documents: 0,
      trips: 0
    }
  }));

  // FetchVehicleDetail com fallback
  const fetchVehicleDetail = params.fetchVehicleDetail ?? (async (vehicleId: string): Promise<VehicleDetailData> => {
    const cached = params.allVehiclesDetails?.get(vehicleId);
    if (cached) return cached;

    const s = vehiclesSummary.find((x) => x.vehicle_id === vehicleId);
    return {
      vehicle_id: vehicleId,
      vehicle_label: s?.vehicle_label ?? `Veículo ${vehicleId}`,
      periodLabel,
      summary: {
        totalCost: s?.cost ?? 0,
        costPerKm: s?.costPerKm ?? 0,
        km: s?.km ?? 0,
        breakdown: (s?.breakdown as VehicleCostBreakdown) ?? {
          fuel: 0, maintenance: 0, tires: 0, expenses: 0, documents: 0, trips: 0,
        },
      },
      fuel: [],
      maintenance: [],
      tires: [],
      expenses: [],
      documents: [],
      trips: [],
    };
  });

  // Insights dinâmicos
  const insights: string[] = [];
  
  if (vehicleRanking.length > 0) {
    const topCost = vehicleRanking[0];
    insights.push(`Veículo com maior custo: ${topCost.vehicle_label} (${moneyBRL(topCost.totalCost)})`);
  }
  
  if (vehicleRanking.length > 0) {
    const topKm = [...vehicleRanking].sort((a, b) => b.km - a.km)[0];
    insights.push(`Veículo com maior quilometragem: ${topKm.vehicle_label} (${numberBR(topKm.km)} km)`);
  }

  const efficientVehicles = vehicleRanking.filter(v => v.efficiency > avgEfficiency * 1.2);
  if (efficientVehicles.length > 0) {
    insights.push(`${efficientVehicles.length} veículos com eficiência acima da média da frota`);
  }

  const highCostVehicles = vehicleRanking.filter(v => v.costPerKm > 2.0);
  if (highCostVehicles.length > 0) {
    insights.push(`${highCostVehicles.length} veículos com custo por km acima de R$ 2,00`);
  }

  if (fuelCost > maintenanceCost * 2) {
    insights.push(`Combustível representa ${((fuelCost / totalCost) * 100).toFixed(0)}% do custo total`);
  }

  // Conclusão
  const conclusionText = `Frota analisada com ${totalVehicles} veículos. Custo total de ${moneyBRL(totalCost)} no período.`;

  return {
    companyName,
    periodLabel,
    generatedAtLabel,
    kpis: {
      totalVehicles,
      activeVehicles,
      totalDistance,
      totalCost,
      fuelCost,
      maintenanceCost,
      avgCostPerKm,
      avgEfficiency,
      avgTripsPerVehicle,
    },
    vehicleRanking,
    insights,
    conclusionText,
    vehiclesSummary,
    fetchVehicleDetail,
    options: params.options ?? { mode: 'top-n', topN: 10, maxVehicles: 80 },
  };
}
